import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Action } from 'redux';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryGroup } from 'victory';

import lang from '../../../../../lib/language';
import { BaseStatementSearchParamType, StatementPeriodType } from '../../../../../lib/models/fundamental/types';
import { RootState } from '../../../../../lib/store';
import { getStatementsCashFlow } from '../../../../../lib/store/fundamental';

import { Button, FinancialPeriodButtons } from '../../../../../components';
import { ButtonTypesEnum } from '../../../../../components/Button/Button.types';
import { useIsMobile } from '../../../../../hooks';
import { UI_ROUTES } from '../../../../../util/routes';
import { cashFlowChartData } from '../Financials.types';

import DataDots from './ChartCommonComponents/DataDots/DataDots';
import OptionsBar from './ChartCommonComponents/OptionsBar/OptionsBar';
import {
  barColorsEnum,
  CHART_HEIGHT,
  emptyCashFlowData,
  getSvgHeightPaddingHeight,
  SVG_HEIGHT,
  xAxisStyle,
  yAxisRightStyle,
} from './Chart.constants';
import {
  formatNumber,
  getBarLocation,
  getBarWidth,
  getChartDomainLength,
  getChartOffset,
  getChartWidth,
  getResultsCount,
} from './helpers';

import styles from './Chart.module.scss';

type Props = {
  symbol: string;
} & ReduxProps;

const CashFlowChart = (props: Props) => {
  const { symbol, getStatements, statementsData } = props;
  const isMobile = useIsMobile();
  const history = useHistory();
  const RESULTS_COUNT = getResultsCount(isMobile);

  const [selectedOption, setSelectedOption] = useState<number>(0);
  const [period, setPeriod] = useState<StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [data, setData] = useState<cashFlowChartData[]>([]);
  const [domainLength, setDomainLength] = useState<number>(0);

  useEffect(() => {
    getStatements({
      symbol,
      periodType: period,
    });
  }, [getStatements, symbol, period]);

  useEffect(() => {
    if (statementsData.cashFlow.length > 0) {
      // Fix React mutation permit by creating a new array
      const reverseData = [...statementsData.cashFlow].reverse();
      const stateData = reverseData.slice(0, RESULTS_COUNT).map((item) => ({
        operating: item.netCashProvidedByOperatingActivities,
        investing: item.netCashUsedForInvestingActivities,
        financing: item.netCashUsedProvidedByFinancingActivities,
        key: item.calendarYear,
        period: item.period,
      }));

      // if the results are less than the RESULTS_COUNT, propagate empty data
      while (stateData.length < RESULTS_COUNT) {
        stateData.push(emptyCashFlowData);
      }
      setData(stateData);
      setDomainLength(stateData.length + 3);
    }
  }, [statementsData, RESULTS_COUNT]);

  const cornerRadius = { topLeft: 4, topRight: 4 };
  const renderBars = (): React.ReactElement[] => {
    const bars: React.ReactElement[] = [];
    data.forEach((item, index) => {
      const barLocation = getBarLocation(isMobile, index);
      bars.push(
        <VictoryBar
          key={`${item.operating}-${item.key}`}
          barWidth={getBarWidth(isMobile, true)}
          data={[{ x: barLocation, y: item.operating }]}
          style={{ data: { fill: barColorsEnum.Primary } }}
          cornerRadius={cornerRadius}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: () => {
                  setSelectedOption(index);
                  return null;
                },
              },
            },
          ]}
        />,
      );
      bars.push(
        <VictoryBar
          key={`${item.investing}-${item.key}`}
          barWidth={getBarWidth(isMobile, true)}
          data={[{ x: barLocation + 0.2, y: item.investing }]}
          style={{ data: { fill: barColorsEnum.Secondary } }}
          cornerRadius={cornerRadius}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: () => {
                  setSelectedOption(index);
                  return null;
                },
              },
            },
          ]}
        />,
      );
      bars.push(
        <VictoryBar
          key={`${item.financing}-${item.key}`}
          barWidth={getBarWidth(isMobile, true)}
          data={[{ x: barLocation + 0.4, y: item.financing }]}
          style={{ data: { fill: barColorsEnum.Tertiary } }}
          cornerRadius={cornerRadius}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: () => {
                  setSelectedOption(index);
                  return null;
                },
              },
            },
          ]}
        />,
      );
    });

    return bars;
  };

  return (
    <>
      <div className={styles.financialTitle}>
        <div>
          {lang.financialsCashFlow()}
        </div>
        <Button
          variant={ButtonTypesEnum.Link}
          className={isMobile ? styles.viewAllFinancialsMobile : styles.viewAll}
          onClick={() => history.push(`${UI_ROUTES.symbol}/${symbol}${UI_ROUTES.cashFlowStatement}`)}
        >
          {lang.commonViewAll()}
        </Button>
      </div>
      <div className={styles.chartContainer}>
        <div className={styles.periodButtons}>
          <FinancialPeriodButtons currentPeriod={period} setPeriod={setPeriod} />
        </div>
        { data.length > 0 ? (
          <div className={styles.chart}>
            <svg
              height={SVG_HEIGHT}
              width="100%"
              preserveAspectRatio="none"
              viewBox={`0 0 ${getChartWidth(isMobile)} ${getSvgHeightPaddingHeight(isMobile)}`}
            >
              <VictoryChart
                standalone={false}
                width={getChartWidth(isMobile)}
                height={CHART_HEIGHT}
                domain={{ x: [0, getChartDomainLength(isMobile, domainLength, true)] }}
              >
                <VictoryAxis
                  style={xAxisStyle}
                />
                <VictoryAxis
                  dependentAxis
                  orientation="right"
                  style={yAxisRightStyle}
                  tickFormat={(tick, index) => formatNumber(tick, index)}
                />
                <VictoryGroup
                  offset={getChartOffset(isMobile, true)}
                >
                  {renderBars()}
                </VictoryGroup>
              </VictoryChart>
            </svg>
            <OptionsBar
              data={data}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              period={period}
            />
          </div>
        ) : (
          <div className={styles.emptyChart} />
        )}
        <DataDots
          titles={[
            `${lang.financialsOperating()}: `,
            `${lang.financialsInvesting()}: `,
            `${lang.financialsFinancing()}: `,
          ]}
          values={[
            formatNumber(data[selectedOption]?.operating || 0),
            formatNumber(data[selectedOption]?.investing || 0),
            formatNumber(data[selectedOption]?.financing || 0),
          ]}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  statementsData: state.fundamental.statementsData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCashFlow(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
    type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CashFlowChart);
